import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Phone, Heart } from 'lucide-react';
import { business, careerImage, careerPerks, careerValues, careerAvailability } from '../mock';
import { createCareer } from '../api';
import useReveal from '../hooks/useReveal';

export default function Careers() {
  const [form, setForm] = useState({ fullName: '', phone: '', email: '', city: '', yearsExperience: '', availability: '', certifications: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useReveal();

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await createCareer(form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Something went wrong. Please try again or give us a call.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section style={{ paddingTop: 74, backgroundColor: 'var(--cream-2)' }}>
        <div className="container-x py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-2">Careers &middot; Become a Caregiver</p>
            <h1 style={{ fontSize: 'clamp(2.2rem, 4.6vw, 3.6rem)' }}>Do the most meaningful work of your life</h1>
            <p className="mt-5 text-lg" style={{ color: 'var(--muted)' }}>
              Join a women-owned team that treats caregivers like family. If you have a caring heart
              and a dependable spirit, we’d love to meet you.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <a href="#apply" className="btn-sage">Apply Now <ArrowRight size={18} /></a>
              <a href={business.phoneHref} className="btn-outline"><Phone size={18} /> {business.phone}</a>
            </div>
          </div>
          <div className="rounded-card" style={{ aspectRatio: '4/3' }}>
            <img src={careerImage} alt="A caregiver sharing a warm moment with a senior" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="section">
        <div className="container-x">
          <div className="text-center max-w-[640px] mx-auto reveal">
            <p className="eyebrow mb-2">Why Join Us</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.6vw, 2.8rem)' }}>Caregiving, the way it should feel</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {careerPerks.map((p) => (
              <div key={p.title} className="reveal p-7 rounded-3xl bg-white" style={{ border: '1px solid var(--line)' }}>
                <span className="flex items-center justify-center rounded-2xl mb-4" style={{ width: 48, height: 48, backgroundColor: 'var(--sage-soft)' }}>
                  <Heart size={24} style={{ color: 'var(--forest)' }} />
                </span>
                <h3 className="text-lg">{p.title}</h3>
                <p className="mt-2 text-[15px]" style={{ color: 'var(--muted)' }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values + Application */}
      <section id="apply" className="section" style={{ backgroundColor: 'var(--cream-2)' }}>
        <div className="container-x grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
          <div className="reveal lg:sticky lg:top-28">
            <p className="eyebrow mb-2">What We Look For</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>The heart of a great caregiver</h2>
            <ul className="flex flex-col gap-4 mt-7">
              {careerValues.map((v) => (
                <li key={v} className="flex items-start gap-3">
                  <span className="flex items-center justify-center rounded-full mt-0.5 shrink-0" style={{ width: 24, height: 24, backgroundColor: 'var(--sage)' }}>
                    <Check size={14} style={{ color: 'var(--forest)' }} strokeWidth={3} />
                  </span>
                  <span style={{ color: 'var(--ink)' }}>{v}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="reveal bg-white rounded-3xl p-8 lg:p-10" style={{ border: '1px solid var(--line)' }}>
            {submitted ? (
              <div className="text-center py-8">
                <span className="flex items-center justify-center rounded-full mx-auto mb-6" style={{ width: 72, height: 72, backgroundColor: 'var(--sage)' }}>
                  <Check size={38} style={{ color: 'var(--forest)' }} strokeWidth={2.5} />
                </span>
                <h3 className="text-2xl">Application received!</h3>
                <p className="mt-3" style={{ color: 'var(--muted)' }}>
                  Thank you for your interest in joining Mom’s Best Friend Senior Care. Our team will
                  review your application and reach out soon.
                </p>
                <a href={business.phoneHref} className="btn-sage mt-7"><Phone size={18} /> Call {business.phone}</a>
              </div>
            ) : (
              <>
                <h3 className="text-2xl">Caregiver Application</h3>
                <p className="mt-2 text-[15px]" style={{ color: 'var(--muted)' }}>Tell us about yourself — it only takes a couple of minutes.</p>
                <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full name" required><input required className="inp" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} /></Field>
                    <Field label="Phone" required><input required className="inp" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Email"><input type="email" className="inp" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
                    <Field label="City"><input className="inp" value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="e.g. Fort Worth" /></Field>
                  </div>
                  <Field label="Years of caregiving experience">
                    <input className="inp" value={form.yearsExperience} onChange={(e) => set('yearsExperience', e.target.value)} placeholder="e.g. 3 years" />
                  </Field>
                  <Field label="Availability">
                    <div className="flex flex-wrap gap-2">
                      {careerAvailability.map((c) => (
                        <button type="button" key={c} onClick={() => set('availability', c)} className="px-4 py-2 rounded-full text-sm font-medium transition-all" style={{ backgroundColor: form.availability === c ? 'var(--forest)' : 'var(--cream-2)', color: form.availability === c ? '#fff' : 'var(--ink)', border: `1px solid ${form.availability === c ? 'var(--forest)' : 'var(--line)'}` }}>{c}</button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Certifications / licenses">
                    <input className="inp" value={form.certifications} onChange={(e) => set('certifications', e.target.value)} placeholder="e.g. CNA, CPR, First Aid" />
                  </Field>
                  <Field label="Anything else you'd like us to know?">
                    <textarea rows={3} className="inp" style={{ resize: 'none' }} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Tell us a bit about yourself and why you love caregiving..." />
                  </Field>
                  <button type="submit" disabled={loading} className="btn-sage justify-center mt-1" style={{ opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Sending…' : 'Submit Application'} <ArrowRight size={18} />
                  </button>
                  {error && <p className="text-sm text-center" style={{ color: '#B4614A' }}>{error}</p>}
                </form>
              </>
            )}
          </div>
        </div>

        <style>{`
          .inp { width: 100%; padding: 12px 16px; border-radius: 12px; border: 1px solid var(--line); background: var(--cream); color: var(--ink); font-size: 15px; font-family: inherit; outline: none; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
          .inp:focus { border-color: var(--forest); box-shadow: 0 0 0 3px rgba(30,59,44,0.1); }
        `}</style>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--forest)' }}>{label} {required && <span style={{ color: '#B4614A' }}>*</span>}</span>
      {children}
    </label>
  );
}
